import { useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import countries from 'i18n-iso-countries';
import frLocale from 'i18n-iso-countries/langs/fr.json';

import { DrawerV2, ButtonV2, dsV2 } from '@bloomays-lib/ui.shared';
import { AddressCountryCodeEU, type ISociety } from '@bloomays-lib/types.shared';
import { UPSERT_BLOOMER_SOCIETY } from '@bloomays-lib/adapter.api-bloomer';

import { cancel, notify } from '../../../../helpers/toastify';
import { errorLogger } from '../../../../helpers/error';
import {
  buildBloomerSocietyUpsertInput,
  validateSupplierSocietyFormat,
  deriveSupplierSocietyMissingFields,
  type SupplierSocietyMissingField,
} from '../../../../helpers/societyForm';
import { useBloomerSocietyForm } from '../../useBloomerSocietyForm';
import { FieldV2, InputV2, SelectV2, Row2, InfoV2, CheckRow, type SelectV2Option } from '../formFields';

countries.registerLocale(frLocale);

export type EditBloomerSocietyDrawerProps = {
  open: boolean;
  onClose: () => void;
  society?: ISociety;
  missionRecordId: string;
};

const MISSING_LABEL: Record<SupplierSocietyMissingField, string> = {
  name: 'Nom',
  status: 'Statut juridique',
  siret: 'SIRET / immatriculation',
  country: "Pays d'immatriculation",
  tva: 'N° TVA',
  iban: 'IBAN',
  bic: 'BIC',
  kbis: 'Extrait KBIS',
  urssaf: 'Attestation URSSAF',
};

/** Drapeau emoji depuis un code ISO-2 (les <option> natives ne peuvent pas afficher d'image). */
const codeToFlag = (iso2: string): string =>
  iso2.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

/**
 * Gestion de la société du Bloomer (fournisseur) en panneau latéral V2 (DrawerV2).
 *
 * Présentation V2 (primitives + Dropzone) branchée sur le hook headless `useBloomerSocietyForm`, donc
 * les règles métier (statut → SIRET / TVA / pays, IBAN/BIC, KBIS / URSSAF / RCP) sont mono-sourcées
 * avec la créa. Spécificités du tableau de bord :
 * - `persist: false` → aucun brouillon en localStorage (édition ponctuelle).
 * - `requireCompleteness: false` → enregistrement PARTIEL autorisé ; ce qui peut manquer est signalé
 *   visuellement (encart + chips), sans bloquer.
 * - le FORMAT des champs renseignés reste validé (SIRET selon le pays, TVA, IBAN/BIC) via les règles
 *   partagées `validateSupplierSocietyFormat` — seule une valeur mal formée bloque la sauvegarde.
 */
export const EditBloomerSocietyDrawer = ({
  open,
  onClose,
  society,
  missionRecordId,
}: EditBloomerSocietyDrawerProps) => {
  const { t } = useTranslation(['notify', 'mission']);
  const succeededRef = useRef(false);

  const [upsertBloomerSociety] = useMutation<{ upsertBloomerSociety: ISociety }>(UPSERT_BLOOMER_SOCIETY);

  const upsertData = async (s: ISociety) => {
    succeededRef.current = false;
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { hideProgressBar: false, autoClose: 10000 });
    try {
      const upload = await upsertBloomerSociety({
        variables: { input: buildBloomerSocietyUpsertInput(s, missionRecordId) },
      });
      cancel();
      notify('success', t('updateSuccess', { ns: 'notify' }), null, { hideProgressBar: true, autoClose: 2000 });
      succeededRef.current = true;
      return upload?.data?.upsertBloomerSociety;
    } catch (error: any) {
      cancel();
      notify('error', t('randomUpdateError', { ns: 'notify' }), error);
      errorLogger(error, { extraInfos: 'editBloomerSocietyDrawerV2' });
    }
  };

  const form = useBloomerSocietyForm({ society, onValidated: upsertData, persist: false, requireCompleteness: false });
  const s = form.formSociety;

  const setField = (patch: Partial<ISociety>) => form.setFormSociety({ ...s, ...patch } as ISociety);

  const statusOptions: SelectV2Option[] = form.selectSocietyOption.map((o) => ({
    value: String(o.value),
    label: typeof o.label === 'string' ? o.label : String(o.value),
    disabled: o.disabled,
  }));

  const allowedCountryCode = (code: string): boolean => {
    if (form.isForeignSociety) return code !== 'FR' && !AddressCountryCodeEU.includes(code);
    if (form.isEuropeanSociety) return code !== 'FR' && AddressCountryCodeEU.includes(code);
    return code === 'FR';
  };
  const countryOptions: SelectV2Option[] = useMemo(() => {
    const names = countries.getNames('fr', { select: 'official' });
    return Object.keys(names)
      .filter(allowedCountryCode)
      .map((code) => ({ value: code, label: `${codeToFlag(code)} ${names[code]}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.isForeignSociety, form.isEuropeanSociety]);

  const registrationLabel =
    form.fieldMode === 'european'
      ? 'N° TVA intracommunautaire'
      : form.fieldMode === 'foreign'
      ? "N° d'entreprise (DUNS, etc.)"
      : 'SIRET ou n° EI';
  const registrationPlaceholder =
    form.fieldMode === 'european'
      ? 'Préfixe pays (2 lettres) + identifiant'
      : form.fieldMode === 'foreign'
      ? 'XXXXXX'
      : '14 chiffres';

  const formatErrors = useMemo(
    () => validateSupplierSocietyFormat(s ?? {}, { fieldMode: form.fieldMode, supportIban: form.supportIban }),
    [s, form.fieldMode, form.supportIban],
  );
  const hasFormatErrors = Object.keys(formatErrors).length > 0;

  const missing = useMemo(
    () => deriveSupplierSocietyMissingFields(s ?? {}, { hasKbis: form.hasKbisDoc, hasUrssaf: form.hasUrssafDoc }),
    [s, form.hasKbisDoc, form.hasUrssafDoc],
  );

  const handleSave = async () => {
    if (hasFormatErrors) return;
    await form.validate({ preventDefault: () => undefined });
    if (succeededRef.current) onClose();
  };

  const ibanLabel = form.supportIban ? t('ibanLabel', { ns: 'mission' }) : t('ibanFallbackLabel', { ns: 'mission' });
  const bicLabel = form.supportIban ? t('bicLabel', { ns: 'mission' }) : t('bicFallbackLabel', { ns: 'mission' });

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title="Société du Bloomer"
      subtitle={society?.name}
      width={520}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            Annuler
          </ButtonV2>
          <ButtonV2
            onClick={handleSave}
            disabled={hasFormatErrors}
            loading={form.uploading}
            startIcon={<SaveRoundedIcon sx={{ fontSize: 15 }} />}
          >
            Enregistrer
          </ButtonV2>
        </>
      }
    >
      <InfoV2 tone="blue" icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}>
        Tu peux enregistrer sans tout remplir — les éléments manquants sont signalés mais non bloquants. Seul un champ
        mal renseigné (SIRET, TVA, IBAN/BIC) empêche l'enregistrement.
      </InfoV2>

      {missing.length > 0 ? (
        <InfoV2 tone="amber">
          À compléter pour la conformité : {missing.map((m) => MISSING_LABEL[m]).join(' · ')}.
        </InfoV2>
      ) : null}

      <FieldV2 label="Nom">
        <InputV2
          value={s?.name ?? ''}
          onChange={(v) => setField({ name: v })}
          placeholder="Raison sociale"
          ariaLabel="bloomerv2-name"
        />
      </FieldV2>

      <CheckRow
        checked={!!s?.portage}
        onChange={(checked) =>
          form.isMicroEntrepreneur && checked
            ? form.setFormSociety({ ...s, status: undefined, portage: true } as ISociety)
            : setField({ portage: checked })
        }
      >
        Société de portage
      </CheckRow>

      <Row2>
        <FieldV2 label="Statut juridique">
          <SelectV2
            value={s?.status ?? ''}
            onChange={(v) => setField({ status: v })}
            options={statusOptions}
            placeholder="Choisir…"
            ariaLabel="bloomerv2-status"
          />
        </FieldV2>
        <FieldV2 label={registrationLabel} error={formatErrors.siret}>
          <InputV2
            value={s?.siret ?? ''}
            onChange={(v) => setField({ siret: v })}
            placeholder={registrationPlaceholder}
            tone={formatErrors.siret ? 'expired' : undefined}
            ariaLabel="bloomerv2-siret"
          />
        </FieldV2>
      </Row2>

      {form.isForeignSociety || form.isEuropeanSociety ? (
        <FieldV2 label="Pays d'immatriculation">
          <SelectV2
            value={s?.addressCountryCode ?? ''}
            onChange={(v) => setField({ addressCountryCode: v })}
            options={countryOptions}
            placeholder="Choisir…"
            ariaLabel="bloomerv2-country"
          />
        </FieldV2>
      ) : null}

      {form.showVATTextField ? (
        <FieldV2
          label="N° TVA"
          error={formatErrors.tva}
          hint={form.isMicroEntrepreneur ? 'Obligatoire si assujetti à la TVA pour traiter tes factures.' : undefined}
        >
          <InputV2
            value={s?.tva ?? ''}
            onChange={(v) => setField({ tva: v })}
            placeholder="FR + 11 chiffres"
            tone={formatErrors.tva ? 'expired' : undefined}
            ariaLabel="bloomerv2-tva"
          />
        </FieldV2>
      ) : null}

      {!s?.portage ? (
        <Row2>
          <FieldV2 label={ibanLabel} error={formatErrors.iban}>
            <InputV2
              value={s?.IBAN ?? ''}
              onChange={(v) => setField({ IBAN: v })}
              placeholder={form.supportIban ? 'FR12 1234 1234 1234 1234 1234 12' : 'XXXXXXXX'}
              tone={formatErrors.iban ? 'expired' : undefined}
              ariaLabel="bloomerv2-iban"
            />
          </FieldV2>
          <FieldV2 label={bicLabel} error={formatErrors.bic}>
            <InputV2
              value={s?.BIC ?? ''}
              onChange={(v) => setField({ BIC: v })}
              placeholder="8 à 11 caractères"
              tone={formatErrors.bic ? 'expired' : undefined}
              ariaLabel="bloomerv2-bic"
            />
          </FieldV2>
        </Row2>
      ) : null}
    </DrawerV2>
  );
};

export default EditBloomerSocietyDrawer;
